import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlay, FaDatabase, FaChartBar, FaArrowRight, FaCheck } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

// ─── Track definitions ────────────────────────────────────────────────────────
const TRACKS = [
  {
    id: 'sql',
    label: 'SQL',
    icon: FaDatabase,
    tagline: 'Write real queries. Solve real cases.',
    description:
      'Business case studies across e-commerce, SaaS, fintech, and product analytics. Run SQL directly in your browser on live datasets.',
    cta: 'Start SQL track',
    ctaLink: '/exercises/thinking-in-data',

  },
  {
    id: 'dataviz',
    label: 'Data Viz',
    icon: FaChartBar,
    tagline: 'Choose the right chart. Tell the story.',
    description:
      'Guided modules covering bar charts, scatter plots, heatmaps, and more. Build visual stories from real datasets that actually communicate insights.',
    cta: 'Start Data Viz track',
    ctaLink: '/exercises/data-viz-intro',

  },
];

const AUTO_MS = 6000;
const STEP_MS = 50;

// ─── SQL code window ──────────────────────────────────────────────────────────
const SQLCodePanel = () => (
  <div className="relative rounded-xl bg-[#0F1623] border border-gray-800 shadow-2xl overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500">
    <div className="flex items-center gap-2 px-4 py-3 bg-[#162032] border-b border-gray-800">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
        <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" />
      </div>
      <div className="ml-4 flex text-xs text-gray-500 font-mono gap-4">
        <span className="text-blue-400">query_editor.sql</span>
        <span>schema.png</span>
      </div>
    </div>
    <div className="p-6 font-mono text-sm leading-relaxed overflow-hidden text-gray-300">
      <div className="text-gray-500 select-none mb-4">-- Calculate monthly revenue growth for 2024</div>
      <div className="space-y-1">
        <div className="text-purple-400">WITH <span className="text-blue-300">monthly_sales</span> AS (</div>
        <div className="pl-4"><span className="text-purple-400">SELECT</span></div>
        <div className="pl-8">
          <span className="text-emerald-400">DATE_TRUNC</span>(<span className="text-orange-300">'month'</span>, <span className="text-blue-300">order_date</span>) <span className="text-purple-400">AS</span> <span className="text-blue-300">sales_month</span>,
        </div>
        <div className="pl-8">
          <span className="text-emerald-400">SUM</span>(<span className="text-blue-300">quantity</span> * <span className="text-blue-300">unit_price</span>) <span className="text-purple-400">AS</span> <span className="text-blue-300">revenue</span>
        </div>
        <div className="pl-4"><span className="text-purple-400">FROM</span> <span className="text-yellow-100">orders</span></div>
        <div className="pl-4">
          <span className="text-purple-400">WHERE</span> <span className="text-emerald-400">EXTRACT</span>(<span className="text-blue-300">year</span> <span className="text-purple-400">FROM</span> <span className="text-blue-300">order_date</span>) = <span className="text-orange-300">2024</span>
        </div>
        <div className="pl-4"><span className="text-purple-400">GROUP BY</span> <span className="text-orange-300">1</span></div>
        <div className="text-purple-400">)</div>
        <div className="mt-2">
          <span className="text-purple-400">SELECT</span> * <span className="text-purple-400">FROM</span> <span className="text-blue-300">monthly_sales</span>;
        </div>
      </div>
      <div className="mt-1 w-2 h-4 bg-blue-500 animate-pulse inline-block align-middle" />
    </div>
    <div className="border-t border-gray-800 bg-[#0B111A] p-4 font-mono text-xs">
      <div className="flex items-center gap-2 text-gray-500 mb-2">
        <div className="w-2 h-2 rounded-full bg-emerald-500" />
        Query executed successfully (0.12s)
      </div>
      <div className="grid grid-cols-2 gap-4 text-gray-400">
        <div className="border-b border-gray-800 pb-1">sales_month</div>
        <div className="border-b border-gray-800 pb-1">revenue</div>
        <div className="text-gray-300">2024-01-01</div>
        <div className="text-emerald-400">$124,500.00</div>
        <div className="text-gray-300">2024-02-01</div>
        <div className="text-emerald-400">$138,250.50</div>
      </div>
    </div>
    <div className="absolute -z-10 top-10 -right-10 w-full h-full border border-gray-800/50 rounded-xl bg-gray-900/50 backdrop-blur-sm" />
  </div>
);

// ─── Data Viz panel – Option B: light analytics card ─────────────────────────
const DataVizPanel = () => (
  <div className="relative rounded-xl bg-white dark:bg-[#0F1623] border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden transform -rotate-1 hover:rotate-0 transition-transform duration-500">
    <style>{`
      @keyframes vizLineGrow {
        from { stroke-dashoffset: 900; }
        to   { stroke-dashoffset: 0; }
      }
      @keyframes vizAreaFade {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes vizKpiUp {
        from { opacity: 0; transform: translateY(5px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes vizDotPop {
        from { opacity: 0; r: 0; }
        to   { opacity: 1; r: 3; }
      }
      .viz-line { stroke-dasharray: 900; animation: vizLineGrow 1.4s cubic-bezier(0.4,0,0.2,1) 0.3s both; }
      .viz-area { animation: vizAreaFade 1.0s ease 1.0s both; }
      .viz-kpi  { animation: vizKpiUp 0.45s ease both; }
      .viz-dot  { animation: vizDotPop 0.25s ease both; }
    `}</style>

    {/* Title bar – mirrors SQL panel */}
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-[#162032] border-b border-gray-200 dark:border-gray-800">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
        <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" />
      </div>
      <div className="ml-4 flex text-xs font-mono gap-4">
        <span className="text-orange-500">revenue_chart.png</span>
        <span className="text-gray-400">monthly_sales</span>
      </div>
    </div>

    {/* KPI tiles */}
    <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-white/8">
      <div className="px-5 py-4 viz-kpi" style={{ animationDelay: '0.1s' }}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Total Revenue</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight leading-none">$842K</p>
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="text-xs font-bold text-emerald-500">↑ 12.3%</span>
          <span className="text-[10px] text-gray-400">vs prev period</span>
        </div>
      </div>
      <div className="px-5 py-4 viz-kpi" style={{ animationDelay: '0.2s' }}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Monthly Avg</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight leading-none">$140K</p>
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="text-[10px] font-mono text-gray-400">Jan – Jun 2024</span>
        </div>
      </div>
    </div>

    {/* Area chart */}
    <svg viewBox="0 0 300 78" className="w-full block" preserveAspectRatio="none" style={{ height: 128 }}>
      <defs>
        <linearGradient id="dvGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f97316" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#f97316" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Subtle horizontal gridlines */}
      {[18, 36, 54].map(y => (
        <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#f0f0f0" strokeWidth="0.6"
          className="dark:hidden" />
      ))}
      {[18, 36, 54].map(y => (
        <line key={y + 'dk'} x1="0" y1={y} x2="300" y2={y} stroke="#ffffff12" strokeWidth="0.6" />
      ))}
      {/* Area fill */}
      <path
        d="M 0,62 C 22,62 38,44 60,44 C 82,44 98,30 120,30 C 142,30 158,50 180,50 C 202,50 218,14 240,14 C 262,14 278,20 300,20 L 300,74 L 0,74 Z"
        fill="url(#dvGrad)"
        className="viz-area"
      />
      {/* Line */}
      <path
        d="M 0,62 C 22,62 38,44 60,44 C 82,44 98,30 120,30 C 142,30 158,50 180,50 C 202,50 218,14 240,14 C 262,14 278,20 300,20"
        fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className="viz-line"
      />
      {/* Data point dots */}
      {[
        [0, 62, 1.35],
        [60, 44, 1.45],
        [120, 30, 1.55],
        [180, 50, 1.65],
        [240, 14, 1.75],
        [300, 20, 1.85],
      ].map(([x, y, delay], i) => (
        <circle key={i} cx={x} cy={y} r="3" fill="#f97316"
          className="viz-dot" style={{ animationDelay: `${delay}s` }} />
      ))}
      {/* Month labels */}
      {[
        [0, 'Jan'], [60, 'Feb'], [120, 'Mar'], [180, 'Apr'], [240, 'May'], [300, 'Jun'],
      ].map(([x, label]) => (
        <text key={label} x={x === 0 ? 8 : x === 300 ? 292 : x}
          y={76} textAnchor={x === 0 ? 'start' : x === 300 ? 'end' : 'middle'}
          fontSize="7" fill="#9ca3af" fontFamily="ui-monospace,monospace">{label}</text>
      ))}
    </svg>

    {/* Footer strip – mirrors SQL panel's result row */}
    <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#0B111A] px-4 py-3 font-mono text-xs flex items-center gap-2">
      <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
      <span className="text-gray-400">from </span>
      <span className="text-orange-400">monthly_sales</span>
      <span className="text-gray-300 dark:text-gray-500 ml-auto">6 rows · 0.12s</span>
    </div>

    {/* Depth shadow card behind (same pattern as SQL panel) */}
    <div className="absolute -z-10 top-10 -left-10 w-full h-full border border-gray-200/50 dark:border-gray-800/50 rounded-xl bg-gray-100/50 dark:bg-gray-900/50" />
  </div>
);

// ─── Hero component ───────────────────────────────────────────────────────────
const Hero = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const progressRef = useRef(0);

  // Auto-rotate tabs with smooth progress bar
  useEffect(() => {
    if (paused) return;
    progressRef.current = 0;
    setProgress(0);
    const stepSize = (STEP_MS / AUTO_MS) * 100;
    const timer = setInterval(() => {
      progressRef.current += stepSize;
      if (progressRef.current >= 100) {
        progressRef.current = 0;
        setProgress(0);
        setActive(a => (a + 1) % TRACKS.length);
      } else {
        setProgress(progressRef.current);
      }
    }, STEP_MS);
    return () => clearInterval(timer);
  }, [active, paused]);

  const handleTabClick = (i) => {
    setActive(i);
    setPaused(false);
    progressRef.current = 0;
    setProgress(0);
  };

  const track = TRACKS[active];

  const handleCTA = () => {
    navigate(user ? track.ctaLink : '/register');
  };

  return (
    <div className="relative overflow-hidden bg-white dark:bg-[#0A1220] text-gray-900 dark:text-white min-h-[100svh] flex items-center border-b border-gray-200 dark:border-gray-800 transition-colors duration-300">
      {/* Background grid - skip on mobile for perf */}
      <div className="absolute inset-0 hidden sm:block bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_70%)]" />

      {/* Glow blobs - reduced blur on mobile */}
      <div className={`absolute top-20 left-1/4 w-64 sm:w-96 h-64 sm:h-96 rounded-full blur-[60px] sm:blur-[100px] transition-all duration-700 ${active === 0 ? 'bg-blue-500/10' : 'bg-orange-500/10'}`} />
      <div className={`absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 rounded-full blur-[60px] sm:blur-[100px] transition-all duration-700 ${active === 0 ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-12 sm:pt-20 sm:pb-10 lg:mt-[-60px] lg:pt-0 lg:pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* ── Left: Copy ── */}
          <div className="flex flex-col justify-center text-left">

            {/* Badges */}
            <div className="flex items-center gap-2 sm:gap-3 mb-5 sm:mb-6 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 border-orange-200 text-orange-600 dark:bg-orange-500/10 dark:border-orange-500/20 dark:text-orange-400 border text-sm font-mono w-fit">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
                </span>
                v1.1 Public Beta
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 border text-sm font-medium w-fit">
                100% Free
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4 sm:mb-5 leading-[1.1]">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400">
                Learn SQL.
              </span>
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400">
                Build Charts.
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-500 to-red-500">
                Think in Data.
              </span>
            </h1>

            {/* Tab selector */}
            <div className="flex mb-5">
              <div className="flex items-center bg-gray-100 dark:bg-white/5 rounded-xl p-1 gap-1 border border-gray-200 dark:border-white/8">
                {TRACKS.map((t, i) => {
                  const Icon = t.icon;
                  const isActive = i === active;
                  return (
                    <button
                      key={t.id}
                      onClick={() => handleTabClick(i)}
                      className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                        isActive
                          ? 'bg-white dark:bg-[#0f1c2e] text-gray-900 dark:text-white shadow-md shadow-black/10'
                          : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <Icon size={12} className={isActive ? 'text-orange-500' : ''} />
                      {t.label}
                      {isActive && (
                        <div className="absolute bottom-0.5 left-3 right-3 h-[2px] rounded-full overflow-hidden bg-gray-200 dark:bg-white/10">
                          <div
                            className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full"
                            style={{ width: `${progress}%`, transition: 'none' }}
                          />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Per-tab content with framer-motion transitions */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.22 }}
              >
                <p className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  {track.tagline}
                </p>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 max-w-md leading-relaxed">
                  {track.description}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleCTA}
                    className="group px-7 py-3.5 bg-gradient-to-r from-[#f97316] to-[#fb923c] hover:from-[#ea580c] hover:to-[#f97316] text-white rounded-xl font-semibold transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
                  >
                    <FaPlay size={11} />
                    {track.cta}
                    <FaArrowRight size={11} className="opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  </button>
                  <Link
                    to="/exercises"
                    className="px-7 py-3.5 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    View all modules
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Trust signals */}
            <div className="mt-7 sm:mt-10 flex items-center gap-3 sm:gap-4 flex-wrap">
              {[
                { label: '100% free', color: 'text-emerald-600 dark:text-emerald-400' },
                { label: '100+ challenges', color: 'text-orange-500 dark:text-orange-400' },
                { label: 'Real datasets', color: 'text-blue-600 dark:text-blue-400' },
              ].map(({ label, color }) => (
                <span key={label} className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                  <FaCheck size={10} className={color} />
                  {label}
                </span>
              ))}
            </div>

            {/* Sandbox nudge */}
            <div className="mt-5 flex items-center gap-2">
              <div className="h-px flex-1 max-w-[40px] bg-gray-200 dark:bg-white/10" />
              <Link
                to="/data-sandbox"
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-orange-500 dark:hover:text-orange-400 transition-colors group"
              >
                <FaDatabase size={9} className="text-teal-500" />
                Have your own data? Try the
                <span className="font-semibold text-gray-600 dark:text-gray-300 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors">Data Sandbox</span>
                <FaArrowRight size={8} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Link>
            </div>
          </div>

          {/* ── Right: animated visual per tab ── */}
          <div className="relative hidden lg:block">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.97, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: -14 }}
                transition={{ duration: 0.32, ease: 'easeOut' }}
              >
                {active === 0 ? <SQLCodePanel /> : <DataVizPanel />}
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Hero;
